import type { StateCreator } from 'zustand';

import { createStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { actionLog, createBoundedUseStore } from '../util';

// ToolCheckpoints slice types
interface Checkpoint {
  action: string;
  content: Record<string, unknown> | string;
  timestamp: Date;
}

interface ToolCheckpointsSlice {
  addCheckpoint: (checkpoint: { content: Record<string, unknown> | string; action: string }) => number;
  checkpoints: Checkpoint[];
  popCheckpoint: () => void;
  resetCheckpoints: () => void;
}

// ChatMessages slice types
interface ChatMessagesSlice {
  curChatId: string | null;
  numMessages: number;
  resetChatMessages: () => void;
  setNumMessages: (count: number) => void;
  updateCurChatId: (chatId: string, messageCount: number) => void;
}

// Combined AI store type
export interface AIStore extends ToolCheckpointsSlice, ChatMessagesSlice {
  reset: () => void;
}

// ToolCheckpoints slice creator
const createToolCheckpointsSlice: StateCreator<
  AIStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  ToolCheckpointsSlice
> = (set, get) => ({
  checkpoints: [],

  addCheckpoint: checkpoint => {
    const newCheckpoint: Checkpoint = {
      ...checkpoint,
      timestamp: new Date(),
    };

    set(
      state => {
        state.checkpoints.push(newCheckpoint);
      },
      false,
      actionLog('addCheckpoint', checkpoint),
    );

    return get().checkpoints.length;
  },

  popCheckpoint: () => {
    set(
      state => {
        state.checkpoints.pop();
      },
      false,
      actionLog('popCheckpoint'),
    );
  },

  resetCheckpoints: () => {
    set(
      state => {
        state.checkpoints = [];
      },
      false,
      actionLog('resetCheckpoints'),
    );
  },
});

// ChatMessages slice creator
const createChatMessagesSlice: StateCreator<
  AIStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  ChatMessagesSlice
> = set => ({
  curChatId: null,
  numMessages: 0,

  updateCurChatId: (chatId: string, messageCount: number) => {
    set(
      state => {
        state.curChatId = chatId;
        state.numMessages = messageCount;
      },
      false,
      actionLog('updateCurChatId', { chatId, messageCount }),
    );
  },

  setNumMessages: (count: number) => {
    set(
      state => {
        state.numMessages = count;
      },
      false,
      actionLog('setNumMessages', count),
    );
  },

  resetChatMessages: () => {
    set(
      state => {
        state.curChatId = null;
        state.numMessages = 0;
      },
      false,
      actionLog('resetChatMessages'),
    );
  },
});

// Initial state
const initialState = {
  checkpoints: [],
  curChatId: null,
  numMessages: 0,
};

/**
 * Vanilla store that contains all AI-related state data including tool checkpoints
 * and chat messages. This store can be accessed and used anywhere. React components
 * should call `useAIStore()` instead.
 * @example
 * import { aiStore } from '@core/store';
 *
 * const checkpoints = aiStore.getState().checkpoints;
 */
export const aiStore = createStore<AIStore>()(
  devtools(
    immer((set, get, ...props) => {
      /**
       * Holds reference to the initial state so we can support resetting the
       * store back to this state when calling `reset()`.
       */
      const resetState = {
        ...createToolCheckpointsSlice(set, get, ...props),
        ...createChatMessagesSlice(set, get, ...props),
        ...initialState,
      };

      return {
        ...resetState,

        reset: () => {
          set(() => resetState, false, actionLog('reset'));
        },
      };
    }),
    { name: 'AIStore' },
  ),
);

/**
 * Bound react hook to access our AI store. Must be called within a React
 * component. To access the store outside of React, use `aiStore` instead.
 * @example
 * import { useAIStore } from '@core/store';
 *
 * function Component() {
 *   const checkpoints = useAIStore(s => s.checkpoints);
 * }
 */
export const useAIStore = createBoundedUseStore(aiStore);
